import React, { useState, useEffect } from 'react';
import { Eye, Download, Filter, Calendar, User, Shield, Check, X, ChevronLeft, ChevronRight, FileText, Search, Loader2 } from 'lucide-react';
import { DecisionBadge } from './PendingApprovalsQueue';
import HRLogo from './HRLogo';
import { useReplica } from '../contexts/ReplicaContext';
import { getApiUrl } from '../lib/env';
import { auth } from '../lib/firebase';

interface AuditLogEntry {
    id: string;
    timestamp: string;
    replicaName: string;
    agentId: string;
    action: string;
    decision: {
        type: 'ai-proposed' | 'human-approved' | 'human-rejected';
        approvedBy?: string;
    };
    sources: string[];
    details?: string;
}

interface AuditLogViewerProps {
    replicaId?: string;
    onBack?: () => void;
}

const API_BASE = getApiUrl();

const AuditLogViewer: React.FC<AuditLogViewerProps> = ({ replicaId: replicaIdProp, onBack }) => {
    const { userReplicas } = useReplica();
    const replicaId = replicaIdProp || userReplicas[0]?.id;
    const replicaName = userReplicas.find(r => r.id === replicaId)?.name || replicaId;

    const [logs, setLogs] = useState<AuditLogEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [filter, setFilter] = useState<'all' | 'ai-proposed' | 'human-approved' | 'human-rejected'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedLog, setExpandedLog] = useState<string | null>(null);
    const logsPerPage = 10;

    useEffect(() => {
        if (!replicaId) return;
        const fetchLogs = async () => {
            setIsLoading(true);
            setFetchError(null);
            try {
                const token = await auth.currentUser?.getIdToken();
                const res = await fetch(`${API_BASE}/audit/${replicaId}?limit=200`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const mapped: AuditLogEntry[] = (data.entries || []).map((e: any) => ({
                    id: e.id,
                    timestamp: new Date(e.timestamp).toLocaleString(),
                    replicaName: replicaName || e.replicaId,
                    agentId: e.agentId,
                    action: e.action,
                    decision: {
                        type: e.decision?.humanInvolved
                            ? (e.decision?.approved ? 'human-approved' : 'human-rejected')
                            : 'ai-proposed',
                        approvedBy: e.decision?.resolvedBy,
                    },
                    sources: Array.isArray(e.sources) ? e.sources : [],
                    details: e.decision?.notes,
                }));
                setLogs(mapped);
            } catch (err) {
                setFetchError('Could not load audit logs from backend.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchLogs();
    }, [replicaId]);

    const filteredLogs = logs.filter(log => {
        const matchesFilter = filter === 'all' || log.decision.type === filter;
        const matchesSearch = searchTerm === '' ||
            log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.replicaName.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const currentLogs = filteredLogs.slice((currentPage - 1) * logsPerPage, currentPage * logsPerPage);

    const handleExport = async (format: 'json' | 'csv') => {
        if (replicaId) {
            try {
                const token = await auth.currentUser?.getIdToken();
                const res = await fetch(`${API_BASE}/audit/${replicaId}/export?format=${format}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (res.ok) {
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `audit-log-export.${format}`;
                    a.click();
                    return;
                }
            } catch { /* fall through to local export */ }
        }
        const data = format === 'json'
            ? JSON.stringify(filteredLogs, null, 2)
            : filteredLogs.map(log => `${log.timestamp},${log.replicaName},${log.action},${log.decision.type}`).join('\n');
        const blob = new Blob([data], { type: format === 'json' ? 'application/json' : 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit-log-export.${format}`;
        a.click();
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-tertiary rounded-lg transition-colors"
                        >
                            <ChevronLeft size={20} />
                        </button>
                    )}
                    <div>
                        <h2 className="text-xl font-semibold text-hig-text-primary flex items-center gap-2.5">
                            <HRLogo
                                state="sovereign"
                                size={40}
                                showGlow={false}
                                aria-label="Audit log — sovereign governance view"
                            />
                            Audit Log
                        </h2>
                        <p className="text-sm text-hig-text-secondary">Complete history of all AI decisions and human approvals</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleExport('json')}
                        className="btn-secondary text-sm flex items-center gap-2"
                    >
                        <Download size={14} />
                        Export JSON
                    </button>
                    <button
                        onClick={() => handleExport('csv')}
                        className="btn-secondary text-sm flex items-center gap-2"
                    >
                        <FileText size={14} />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
                <div className="relative flex-1 max-w-xs">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" />
                    <input
                        type="text"
                        placeholder="Search actions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-primary pl-9 w-full"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter size={14} className="text-hig-text-tertiary" />
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value as typeof filter)}
                        className="bg-hig-bg-tertiary border border-hig-border rounded-lg px-3 py-2 text-sm text-hig-text-secondary focus:outline-none focus:ring-1 focus:ring-hig-accent"
                    >
                        <option value="all">All Decisions</option>
                        <option value="ai-proposed">AI Proposed</option>
                        <option value="human-approved">Human Approved</option>
                        <option value="human-rejected">Human Rejected</option>
                    </select>
                </div>
            </div>

            {/* Loading / Error states */}
            {isLoading && (
                <div className="flex items-center gap-2 text-hig-text-secondary text-sm py-2">
                    <Loader2 size={16} className="animate-spin" /> Loading audit logs...
                </div>
            )}
            {fetchError && (
                <div className="text-hig-error text-sm py-2">{fetchError}</div>
            )}
            {!isLoading && !fetchError && !replicaId && (
                <div className="text-hig-text-tertiary text-sm py-2">No replica selected. Create a replica to view audit logs.</div>
            )}

            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-4">
                <div className="card p-4 text-center">
                    <p className="text-2xl font-bold text-hig-text-primary">{logs.filter(l => l.decision.type === 'ai-proposed').length}</p>
                    <p className="text-xs text-hig-text-tertiary">AI Decisions</p>
                </div>
                <div className="card p-4 text-center">
                    <p className="text-2xl font-bold text-hig-success">{logs.filter(l => l.decision.type === 'human-approved').length}</p>
                    <p className="text-xs text-hig-text-tertiary">Human Approved</p>
                </div>
                <div className="card p-4 text-center">
                    <p className="text-2xl font-bold text-hig-error">{logs.filter(l => l.decision.type === 'human-rejected').length}</p>
                    <p className="text-xs text-hig-text-tertiary">Human Rejected</p>
                </div>
            </div>

            {/* Log Table */}
            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-hig-bg-tertiary border-b border-hig-divider">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Timestamp</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Replica</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Action</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Decision</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-hig-text-tertiary uppercase tracking-wider">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-hig-divider">
                            {currentLogs.map((log) => (
                                <React.Fragment key={log.id}>
                                    <tr className="hover:bg-hig-bg-tertiary/50 cursor-pointer" onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2 text-sm text-hig-text-secondary">
                                                <Calendar size={12} className="text-hig-text-tertiary" />
                                                {log.timestamp}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-full bg-hig-accent/10 flex items-center justify-center">
                                                    <User size={12} className="text-hig-accent" />
                                                </div>
                                                <span className="text-sm font-medium text-hig-text-primary">{log.replicaName}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="text-sm text-hig-text-primary max-w-xs truncate">{log.action}</p>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <DecisionBadge type={log.decision.type} />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <button className="text-hig-accent hover:underline text-sm flex items-center gap-1">
                                                <Eye size={12} />
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedLog === log.id && (
                                        <tr>
                                            <td colSpan={5} className="px-4 py-4 bg-hig-bg-tertiary">
                                                <div className="space-y-3">
                                                    <div>
                                                        <p className="text-xs text-hig-text-tertiary mb-1">Full Action</p>
                                                        <p className="text-sm text-hig-text-primary">{log.action}</p>
                                                    </div>
                                                    {log.details && (
                                                        <div>
                                                            <p className="text-xs text-hig-text-tertiary mb-1">Details</p>
                                                            <p className="text-sm text-hig-text-secondary">{log.details}</p>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="text-xs text-hig-text-tertiary mb-1">Data Sources</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {log.sources.map((source, i) => (
                                                                <span key={i} className="px-2 py-0.5 bg-hig-bg-secondary rounded text-xs text-hig-text-secondary">
                                                                    {source}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {log.decision.approvedBy && (
                                                        <div>
                                                            <p className="text-xs text-hig-text-tertiary mb-1">
                                                                {log.decision.type === 'human-approved' ? 'Approved by' : 'Rejected by'}
                                                            </p>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-6 h-6 rounded-full bg-hig-success/10 flex items-center justify-center">
                                                                    {log.decision.type === 'human-approved' ? (
                                                                        <Check size={12} className="text-hig-success" />
                                                                    ) : (
                                                                        <X size={12} className="text-hig-error" />
                                                                    )}
                                                                </div>
                                                                <span className="text-sm text-hig-text-primary">{log.decision.approvedBy}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="pt-2 border-t border-hig-divider">
                                                        <p className="text-xs text-hig-text-tertiary flex items-center gap-1">
                                                            <Shield size={10} />
                                                            Signature verified • Entry ID: {log.id}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-hig-divider">
                        <p className="text-sm text-hig-text-tertiary">
                            Showing {(currentPage - 1) * logsPerPage + 1} to {Math.min(currentPage * logsPerPage, filteredLogs.length)} of {filteredLogs.length}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-2 text-hig-text-tertiary hover:text-hig-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span className="text-sm text-hig-text-secondary">
                                {currentPage} / {totalPages}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 text-hig-text-tertiary hover:text-hig-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Compact preview component for dashboard embedding
export const AuditLogPreview: React.FC<{ onViewAll?: () => void }> = ({ onViewAll }) => {
    const { userReplicas } = useReplica();
    const replicaId = userReplicas[0]?.id;
    const [recentLogs, setRecentLogs] = React.useState<AuditLogEntry[]>([]);

    React.useEffect(() => {
        if (!replicaId) return;
        const load = async () => {
            try {
                const token = await auth.currentUser?.getIdToken();
                const res = await fetch(`${API_BASE}/audit/${replicaId}?limit=5`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) return;
                const data = await res.json();
                const replicaName = userReplicas[0]?.name || replicaId;
                setRecentLogs((data.entries || []).map((e: any) => ({
                    id: e.id,
                    timestamp: new Date(e.timestamp).toLocaleString(),
                    replicaName,
                    agentId: e.agentId,
                    action: e.action,
                    decision: {
                        type: e.decision?.humanInvolved
                            ? (e.decision?.approved ? 'human-approved' : 'human-rejected')
                            : 'ai-proposed',
                    },
                    sources: [],
                })));
            } catch { /* silent */ }
        };
        load();
    }, [replicaId]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-hig-text-primary flex items-center gap-2">
                    <HRLogo state="sovereign" size={20} showGlow={false} />
                    Recent Audit Events
                </h3>
                {onViewAll && (
                    <button
                        onClick={onViewAll}
                        className="text-sm text-hig-accent hover:underline"
                    >
                        View All
                    </button>
                )}
            </div>
            <div className="space-y-2">
                {recentLogs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-3 bg-hig-bg-tertiary rounded-lg">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-sm font-medium text-hig-text-primary">{log.replicaName}</span>
                            <span className="text-sm text-hig-text-secondary truncate">{log.action}</span>
                        </div>
                        <DecisionBadge type={log.decision.type} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AuditLogViewer;
